import { useEffect, useMemo, useState } from 'react';
import { PiArrowsDownUpDuotone, PiArrowUpDuotone } from 'react-icons/pi';

import { PopupLoader } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

const ALIGN_CLASS_MAP = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
};

function normalizeSortValue(value) {
    if (value === null || value === undefined || value === '') { return null; }
    if (value instanceof Date) { return value.getTime(); }
    if (typeof value === 'number' || typeof value === 'boolean') { return value; }

    if (typeof value === 'string') {
        const parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) { return parsedDate.getTime(); }
        return value.toLowerCase();
    }

    return String(value).toLowerCase();
}

function compareValues(a, b) {
    if (a === b) { return 0; }
    if (a === null || a === undefined || a === '') { return 1; }
    if (b === null || b === undefined || b === '') { return -1; }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}

function resolveSortFromProps(propsSort, configSort) {
    const source = propsSort || configSort || {};

    return {
        key: source.key || null,
        direction: source.direction || 'asc'
    };
}

function getNextSort(prevSort, column) {
    if (!column.sortable) return prevSort;

    if (prevSort?.key !== column.key) {
        return {
            key: column.key,
            direction: column.defaultSortDirection || 'asc'
        };
    }

    return {
        key: column.key,
        direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
    };
}

export default function OptimizationPipelinePopupTable(props) {
    const {
        tableConfig,
        rows = [],
        loading = false,
        error = null,
        className,
        minHeight = 320,
        rowKey = 'id',
        sort: controlledSort = null,
        onSortChange
    } = props;

    const {
        columns = [],
        sort: configSort = {},
        states = {},
        tableMinWidth = null
    } = tableConfig || {};

    const resolvedSort = useMemo(() => {
        return resolveSortFromProps(controlledSort, configSort);
    }, [controlledSort, configSort]);

    const [activeSort, setActiveSort] = useState(resolvedSort);

    useEffect(() => {
        setActiveSort(resolvedSort);
    }, [resolvedSort]);

    const preparedRows = useMemo(() => {
        let nextRows = rows;

        if (activeSort?.key) {
            const sortColumn = columns.find((column) => column.key === activeSort.key);

            if (sortColumn?.sortable) {
                nextRows = [].concat(nextRows).sort((rowA, rowB) => {
                    const valueA = typeof sortColumn.getSortValue === 'function' ? sortColumn.getSortValue(rowA) : rowA?.[sortColumn.key];
                    const valueB = typeof sortColumn.getSortValue === 'function' ? sortColumn.getSortValue(rowB) : rowB?.[sortColumn.key];
                    const comparison = compareValues(normalizeSortValue(valueA), normalizeSortValue(valueB));

                    return activeSort.direction === 'desc' ? comparison * -1 : comparison;
                });
            }
        }

        return nextRows;
    }, [activeSort, columns, rows]);

    const handleSort = (column) => {
        if (!column.sortable) { return; }

        const nextSort = getNextSort(activeSort, column);

        setActiveSort(nextSort);
        onSortChange?.(nextSort);
    };

    const hasRows = rows.length > 0;
    const hasPreparedRows = preparedRows.length > 0;
    const hasHorizontalScroll = !!tableMinWidth;

    if (loading) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <PopupLoader label={states.loadingTitle || 'Loading data...'} minHeight={minHeight} />
            </div>
        );
    }

    if (error) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium' style={{ minHeight: minHeight }}>
                    {states.errorTitle || 'Something went wrong'}
                </div>
            </div>
        );
    }

    if (!hasRows) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium' style={{ minHeight: minHeight }}>
                    {states.emptyTitle || 'No data available'}
                </div>
            </div>
        );
    }

    if (!hasPreparedRows) {
        return (
            <div className={'w-full h-full min-h-0 flex flex-col p-16 ' + (className || '')}>
                <div className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium' style={{ minHeight: minHeight }}>
                    {states.noResultsTitle || 'No matching results'}
                </div>
            </div>
        );
    }

    return (
        <div className={'flex h-full min-h-0 flex-col overflow-hidden ' + (className || '')}>
            <div className='min-h-0 flex-1 flex flex-col overflow-auto'>
                <table className={'border-collapse ' + (hasHorizontalScroll ? 'table-auto' : 'w-full table-fixed')} style={hasHorizontalScroll ? { minWidth: tableMinWidth } : undefined}>
                    <thead className='sticky top-0 z-10 bg-grey-50'>
                        <tr className='border-b border-grey-200'>
                            {columns.map((column) => {
                                const headerAlignClassName = ALIGN_CLASS_MAP[column.headerAlign || column.align || 'left'] || ALIGN_CLASS_MAP.left;
                                const isActiveSortColumn = activeSort?.key === column.key;

                                return (
                                    <th
                                        key={column.key}
                                        onClick={() => handleSort(column)}
                                        className={
                                            'group border-b border-grey-200 px-16 py-16 text-xs font-medium tracking-wide ' +
                                            headerAlignClassName + ' ' +
                                            (column.sortable ? 'cursor-pointer select-none' : '') + ' ' +
                                            (column.className || '')
                                        }
                                        style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                        title={column.sortable ? 'Sort' : undefined}
                                    >
                                        <div className={'flex items-center gap-6 ' + ((column.headerAlign || column.align) === 'right' ? 'justify-end' : (column.headerAlign || column.align) === 'center' ? 'justify-center' : 'justify-start')}>
                                            <span className='truncate'>{column.label}</span>

                                            {column.sortable ? (
                                                <span className={'flex shrink-0 items-center justify-center text-sm transition-all duration-200 ' + (isActiveSortColumn ? '' : 'opacity-50')}>
                                                    {isActiveSortColumn ? (
                                                        <PiArrowUpDuotone className={'transform transition-all duration-200 scale-110 ' + (activeSort.direction === 'desc' ? 'rotate-180' : 'rotate-0')} />
                                                    ) : (
                                                        <PiArrowsDownUpDuotone className='transition-opacity duration-200 opacity-50 group-hover:opacity-100' />
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {preparedRows.map((row, index) => {
                            const resolvedRowKey = typeof rowKey === 'function' ? rowKey(row, index) : (row?.[rowKey] ?? row?.key ?? row?.id ?? index);

                            return (
                                <tr key={resolvedRowKey} className={'border-b border-dashed border-grey-200 transition odd:bg-grey-50 ' + (index % 2 === 1 ? 'bg-grey-50' : '')}>
                                    {columns.map((column) => {
                                        const alignClassName = ALIGN_CLASS_MAP[column.align || 'left'] || ALIGN_CLASS_MAP.left;
                                        const content = typeof column.renderCell === 'function' ? column.renderCell({ row: row, column: column, rowIndex: index }) : row?.[column.key];

                                        return (
                                            <td
                                                key={column.key}
                                                className={
                                                    'px-16 py-16 text-xs text-grey-700 align-top ' +
                                                    alignClassName + ' ' +
                                                    (column.cellClassName || '')
                                                }
                                                style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                                            >
                                                {content}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function PopupTableActionButton(props) {
    const {
        label = 'View',
        active = false,
        disabled = false,
        title,
        onClick,
        className = ''
    } = props;

    return (
        <button
            type='button'
            disabled={disabled}
            title={title}
            className={
                'rounded-lg border px-16 py-8 text-10 leading-none font-medium transition ' +
                (active ? 'bg-black text-white border-black' : 'bg-transparent text-black/70 border-gray-100 hover:bg-gray-50') + ' ' +
                (disabled ? 'opacity-50 pointer-events-none' : '') + ' ' +
                className
            }
            onClick={onClick}
        >
            {label}
        </button>
    );
}